import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './Card';
import { attemptChunkReloadRecovery, isDynamicImportFailure } from '@/utils/chunkLoadRecovery';

import { secureLogger } from '@/utils/secureLogger';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (attemptChunkReloadRecovery('error-boundary', error)) {
      return;
    }

    secureLogger.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <Card variant="elevated" className="max-w-2xl w-full">
            <CardHeader>
              <CardTitle as="h2" className="text-red-600">
                Something went wrong
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <p className="text-gray-700 mb-4">
                {this.state.error && isDynamicImportFailure(this.state.error)
                  ? 'A new version of the app was detected while this page was open. Please refresh to load the latest files.'
                  : "We're sorry, but something unexpected happened. Please try refreshing the page."}
              </p>
              
              {this.state.error && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 font-medium">
                    Error details
                  </summary>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-mono text-red-600 mb-2">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="text-xs text-gray-600 overflow-auto max-h-64">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </CardContent>
            
            <CardFooter>
              <div className="flex gap-3">
                <Button onClick={this.handleReset} variant="primary">
                  Try again
                </Button>
                <Button onClick={() => window.location.reload()} variant="secondary">
                  Refresh page
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  error?: Error;
  onRetry?: () => void;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Error',
  message,
  error,
  onRetry,
  className = '',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center py-12 ${className}`.trim()}>
      <div className="text-red-500 mb-4">
        <svg
          className="h-16 w-16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-center max-w-md mb-6">{message}</p>
      
      {error && import.meta.env.DEV && (
        <details className="mb-6">
          <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 font-medium">
            Error details
          </summary>
          <pre className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200 text-xs text-red-600 overflow-auto max-w-2xl">
            {error.toString()}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
      
      {onRetry && (
        <Button onClick={onRetry} variant="primary">
          Try again
        </Button>
      )}
    </div>
  );
};


